/**
 * PaymentExchangeRateLoader
 *
 * Web port of Flutter's `_getLocalCurrencyAmount` from
 * `lib/presentation/screens/main/main_screen.dart:207-220`. Fetches the
 * Revolut exchange rate from USD to the wallet's local currency once the
 * currency is known, and stores it in the payment slice. Renders nothing —
 * mount alongside the other app-level listeners.
 *
 * Rationale: Flutter fires once at boot in `_onBuildCompleted` and races
 * the wallet listener — the wallet's `currency` may not be set yet at that
 * instant. We instead wait for `currency` to be available, which fixes the
 * latent Flutter race without changing user-visible behavior.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/presentation/store';
import { selectWallet } from '@/presentation/store/slices/wallet/walletSlice';
import {
  clearExchangeRate,
  selectExchangeRate,
  selectRevolutEnvironment,
  setExchangeRate,
} from '@/presentation/store/slices/payment/paymentSlice';
import { refreshLocalCurrencyExchangeRateUseCase } from '@/domain/usecases/payment/refreshLocalCurrencyExchangeRateUseCase';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';

export const PaymentExchangeRateLoader = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isInitializing = useSelector((state: RootState) => state.auth.isInitializing);
  const wallet = useSelector(selectWallet);
  const exchangeRate = useSelector(selectExchangeRate);
  const revolutEnvironment = useSelector(selectRevolutEnvironment);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(clearExchangeRate());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || isInitializing) return;
    const currency = wallet?.currency;
    if (!currency) return;
    if (exchangeRate !== null) return;

    void (async () => {
      const result = await refreshLocalCurrencyExchangeRateUseCase.execute(
        currency,
        revolutEnvironment,
        // Flutter: `RemoteConfigService.defaultCurrency` (payment_utils.dart).
        remoteConfigService.defaultCurrency,
      );
      if (result !== null) {
        dispatch(setExchangeRate(result));
      }
    })();
  }, [isAuthenticated, isInitializing, wallet?.currency, exchangeRate, revolutEnvironment, dispatch]);

  return null;
};
